import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from 'react-router-dom';
import { getLevels, editImpact, editProbability } from "../../store/actions/riskActions";
import { LuPencil, LuX, LuCheck } from "react-icons/lu";

const colorsProbabilities = [
  { value: 'Rare', color: "#0081A7" },
  { value: 'Unlikely', color: "#00AFB9" },
  { value: 'Possible', color: "#F59E0B" },
  { value: 'Likely', color: "#F07167" },
  { value: 'Almost certain', color: "#b4170b" },
];

const colorsImpacts = [
  { value: 'Insignificant', color: "#0081A7" },
  { value: 'Minor', color: "#00AFB9" },
  { value: 'Moderate', color: "#F59E0B" },
  { value: 'Major', color: "#F07167" },
  { value: 'Catastrophe', color: "#b4170b" },
];

const PROBABILITY_SCALE_TO_KEY = {
  "Almost certain": "almostCertain",
  Likely: "likely",
  Possible: "possible",
  Unlikely: "unlikely",
  Rare: "rare",
};

const IMPACT_NAME_TO_KEY = {
  Insignificant: "insignificant",
  Minor: "minor",
  Moderate: "moderate",
  Major: "major",
  Catastrophe: "catastrophe",
};

function translateProbabilityScale(scale, t) {
  const k = PROBABILITY_SCALE_TO_KEY[scale];
  return k ? t(`riskCenterPortal.riskAssessment.probability.scales.${k}`) : scale;
}

function translateImpactName(name, t) {
  const k = IMPACT_NAME_TO_KEY[name];
  return k ? t(`riskCenterPortal.riskAssessment.impact.scales.${k}`) : name;
}

function ModalShell({ title, subtitle, score, color, onClose, children, onSave }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-bold flex-shrink-0"
              style={{ backgroundColor: `${color}22`, color }}
            >
              {score}
            </span>
            <div>
              <p className="text-[15px] font-semibold text-gray-900">{title}</p>
              <p className="text-[12px] text-gray-400">{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <LuX className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {children}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {t("riskCenterPortal.riskAssessment.modals.cancel")}
          </button>
          <button
            onClick={onSave}
            className="h-9 px-5 rounded-lg bg-gray-900 text-[13px] text-white hover:bg-gray-700 transition-colors flex items-center gap-1.5"
          >
            <LuCheck className="w-3.5 h-3.5" />
            {t("riskCenterPortal.riskAssessment.modals.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-gray-200 text-[13px] text-gray-800 focus:outline-none focus:border-gray-400 transition-colors";
const textareaCls = "w-full px-3 py-2.5 rounded-lg border border-gray-200 text-[13px] text-gray-800 focus:outline-none focus:border-gray-400 transition-colors resize-none";

function ProbabilityModal({ probability, index, onSave, onClose }) {
  const { t } = useTranslation();
  const color = colorsProbabilities.find(c => c.value === probability.scale)?.color || '#6b7280';
  const [frequency, setFrequency] = useState(probability.frequency || "");
  const [controls, setControls] = useState(probability.controls || "");

  return (
    <ModalShell
      title={translateProbabilityScale(probability.scale, t)}
      subtitle={t("riskCenterPortal.riskAssessment.probability.modal.subtitle")}
      score={index + 1}
      color={color}
      onClose={onClose}
      onSave={() => onSave(frequency, controls)}
    >
      <Field label={t("riskCenterPortal.riskAssessment.probability.modal.frequencyLabel")}>
        <input
          className={inputCls}
          value={frequency}
          onChange={e => setFrequency(e.target.value)}
          placeholder={t("riskCenterPortal.riskAssessment.probability.modal.frequencyPlaceholder")}
        />
      </Field>
      <Field label={t("riskCenterPortal.riskAssessment.probability.modal.descriptionLabel")}>
        <textarea
          className={textareaCls}
          rows={3}
          value={controls}
          onChange={e => setControls(e.target.value)}
          placeholder={t("riskCenterPortal.riskAssessment.probability.modal.descriptionPlaceholder")}
        />
      </Field>
    </ModalShell>
  );
}

function ImpactModal({ impact, index, onSave, onClose }) {
  const { t } = useTranslation();
  const color = colorsImpacts.find(c => c.value === impact.impact)?.color || '#6b7280';
  const [operational, setOperational] = useState(impact.operational || "");
  const [reputational, setReputational] = useState(impact.reputational || "");
  const [strategic, setStrategic] = useState(impact.strategic || "");
  const [financialLow, setFinancialLow] = useState(impact.financial_low ?? "");
  const [financialHigh, setFinancialHigh] = useState(impact.financial_high ?? "");

  return (
    <ModalShell
      title={translateImpactName(impact.impact, t)}
      subtitle={t("riskCenterPortal.riskAssessment.impact.modal.subtitle")}
      score={index + 1}
      color={color}
      onClose={onClose}
      onSave={() => onSave(operational, financialLow, financialHigh, reputational, strategic)}
    >
      <Field label={t("riskCenterPortal.riskAssessment.impact.modal.operationalLabel")}>
        <textarea className={textareaCls} rows={2} value={operational} onChange={e => setOperational(e.target.value)} placeholder={t("riskCenterPortal.riskAssessment.impact.modal.operationalPlaceholder")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("riskCenterPortal.riskAssessment.impact.modal.financialLow")}>
          <input type="number" className={inputCls} value={financialLow} onChange={e => setFinancialLow(e.target.value)} placeholder="0" />
        </Field>
        <Field label={t("riskCenterPortal.riskAssessment.impact.modal.financialHigh")}>
          <input type="number" className={inputCls} value={financialHigh} onChange={e => setFinancialHigh(e.target.value)} placeholder="0" />
        </Field>
      </div>
      <Field label={t("riskCenterPortal.riskAssessment.impact.modal.reputationalLabel")}>
        <textarea className={textareaCls} rows={2} value={reputational} onChange={e => setReputational(e.target.value)} placeholder={t("riskCenterPortal.riskAssessment.impact.modal.reputationalPlaceholder")} />
      </Field>
      <Field label={t("riskCenterPortal.riskAssessment.impact.modal.strategicLabel")}>
        <textarea className={textareaCls} rows={2} value={strategic} onChange={e => setStrategic(e.target.value)} placeholder={t("riskCenterPortal.riskAssessment.impact.modal.strategicPlaceholder")} />
      </Field>
    </ModalShell>
  );
}

function EmptyCell() {
  return <span className="text-gray-300 text-[12px]">—</span>;
}

function formatFinancial(impact) {
  const low = impact.financial_low;
  const high = impact.financial_high;
  if (impact.impact === 'Catastrophe') {
    return low != null && low !== '' ? `> $${low}` : null;
  }
  if (impact.impact === 'Insignificant') {
    return high != null && high !== '' ? `< $${high}` : null;
  }
  const hasLow = low != null && low !== '';
  const hasHigh = high != null && high !== '';
  if (!hasLow && !hasHigh) return null;
  return `$${hasLow ? low : '—'} – $${hasHigh ? high : '—'}`;
}

function RiskLevels(props) {
  const { t } = useTranslation();
  const { token, levels, getLevelsFN, editImpactFN, editProbabilityFN, company_id, hideHeader } = props;

  const [editingProbability, setEditingProbability] = useState(null);
  const [editingImpact, setEditingImpact] = useState(null);

  useEffect(() => {
    getLevelsFN(company_id);
  }, [getLevelsFN, company_id]);

  const handleSaveProbability = (frequency, controls) => {
    const { probability } = editingProbability;
    editProbabilityFN(probability.id, controls, frequency, company_id);
    setEditingProbability(null);
  };

  const handleSaveImpact = (operational, financialLow, financialHigh, reputational, strategic) => {
    const { impact } = editingImpact;
    editImpactFN(
      impact.id,
      operational,
      parseInt(financialLow) || '',
      parseInt(financialHigh) || '',
      reputational,
      strategic,
      company_id
    );
    setEditingImpact(null);
  };

  return token ? (
    <>
      {editingProbability && (
        <ProbabilityModal
          probability={editingProbability.probability}
          index={editingProbability.index}
          onSave={handleSaveProbability}
          onClose={() => setEditingProbability(null)}
        />
      )}

      {editingImpact && (
        <ImpactModal
          impact={editingImpact.impact}
          index={editingImpact.index}
          onSave={handleSaveImpact}
          onClose={() => setEditingImpact(null)}
        />
      )}

      <div className="flex-none w-full px-4 md:px-6 py-2 md:py-4 overflow-auto">
        {!hideHeader && (
          <div className="mb-8">
            <h1 className="text-xl font-semibold text-gray-900 tracking-tight">{t("riskCenterPortal.riskAssessment.pageTitle")}</h1>
            <p className="text-[13px] text-gray-400 mt-1">{t("riskCenterPortal.riskAssessment.pageSubtitle")}</p>
          </div>
        )}

        {/* Probability table */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm mb-8">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-semibold text-gray-900">{t("riskCenterPortal.riskAssessment.probability.sectionTitle")}</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">{t("riskCenterPortal.riskAssessment.probability.sectionSubtitle")}</p>
            </div>
            {levels?.riskProbabilities?.length > 0 && (
              <span className="text-[12px] text-gray-400">
                {t("riskCenterPortal.riskAssessment.probability.levelsCount", { count: levels.riskProbabilities.length })}
              </span>
            )}
          </div>

          {levels?.riskProbabilities?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">{t("riskCenterPortal.riskAssessment.probability.columns.level")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">{t("riskCenterPortal.riskAssessment.probability.columns.score")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-40">{t("riskCenterPortal.riskAssessment.probability.columns.percentage")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{t("riskCenterPortal.riskAssessment.probability.columns.description")}</th>
                    <th className="px-6 py-3 w-20" />
                  </tr>
                </thead>
                <tbody>
                  {levels.riskProbabilities.map((probability, index) => {
                    const color = colorsProbabilities.find(c => c.value === probability.scale)?.color || '#6b7280';
                    return (
                      <tr key={index} className="border-t border-gray-100 hover:bg-gray-50/60 group transition-colors">
                        <td className="px-6 py-3.5">
                          <span
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-semibold"
                            style={{ backgroundColor: `${color}18`, color }}
                          >
                            {translateProbabilityScale(probability.scale, t)}
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold"
                            style={{ backgroundColor: `${color}22`, color }}
                          >
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-[13px] text-gray-600">
                          {probability.frequency ? probability.frequency : <EmptyCell />}
                        </td>
                        <td className="px-6 py-3.5 text-[13px] text-gray-600 max-w-xs">
                          {probability.controls ? probability.controls : <EmptyCell />}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <button
                            onClick={() => setEditingProbability({ probability, index })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <LuPencil className="w-3.5 h-3.5" />
                            {t("riskCenterPortal.riskAssessment.probability.edit")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Impact table */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-semibold text-gray-900">{t("riskCenterPortal.riskAssessment.impact.sectionTitle")}</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">{t("riskCenterPortal.riskAssessment.impact.sectionSubtitle")}</p>
            </div>
            {levels?.riskImpacts?.length > 0 && (
              <span className="text-[12px] text-gray-400">
                {t("riskCenterPortal.riskAssessment.impact.levelsCount", { count: levels.riskImpacts.length })}
              </span>
            )}
          </div>

          {levels?.riskImpacts?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-36">{t("riskCenterPortal.riskAssessment.impact.columns.level")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20">{t("riskCenterPortal.riskAssessment.impact.columns.score")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{t("riskCenterPortal.riskAssessment.impact.columns.operational")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-44">{t("riskCenterPortal.riskAssessment.impact.columns.financial")}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{t("riskCenterPortal.riskAssessment.impact.columns.reputational")}</th>
                    <th className="px-6 py-3 w-20" />
                  </tr>
                </thead>
                <tbody>
                  {levels.riskImpacts.map((impact, index) => {
                    const color = colorsImpacts.find(c => c.value === impact.impact)?.color || '#6b7280';
                    const financial = formatFinancial(impact);
                    return (
                      <tr key={index} className="border-t border-gray-100 hover:bg-gray-50/60 group transition-colors">
                        <td className="px-6 py-3.5">
                          <span
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-semibold"
                            style={{ backgroundColor: `${color}18`, color }}
                          >
                            {translateImpactName(impact.impact, t)}
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold"
                            style={{ backgroundColor: `${color}22`, color }}
                          >
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-[13px] text-gray-600 max-w-[160px]">
                          {impact.operational ? impact.operational : <EmptyCell />}
                        </td>
                        <td className="px-6 py-3.5 text-[13px] text-gray-600 font-mono">
                          {financial ? financial : <EmptyCell />}
                        </td>
                        <td className="px-6 py-3.5 text-[13px] text-gray-600 max-w-[160px]">
                          {impact.reputational ? impact.reputational : <EmptyCell />}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <button
                            onClick={() => setEditingImpact({ impact, index })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <LuPencil className="w-3.5 h-3.5" />
                            {t("riskCenterPortal.riskAssessment.impact.edit")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  ) : (
    <Redirect to="/login" />
  );
}

const mapStateToProps = (state) => ({
  token: state.auth.token,
  levels: state.risk.levels,
});

const mapDispatchToProps = (dispatch) => ({
  getLevelsFN: (id) => dispatch(getLevels(id)),
  editImpactFN: (impact_id, operational, financial_low, financial_high, reputational, strategic, id) =>
    dispatch(editImpact(impact_id, operational, financial_low, financial_high, reputational, strategic, id)),
  editProbabilityFN: (probability_id, controls, frequency, id) =>
    dispatch(editProbability(probability_id, controls, frequency, id)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(RiskLevels);
